// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Button, Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconClipboardText } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useNavigate } from 'react-router';
import { useIntakeQuestionnaireOutstanding } from '../../hooks/useIntakeQuestionnaireOutstanding';

/**
 * Dashboard prompt for a patient who booked their intake appointment but has not
 * completed the sleep-history questionnaire. Opens the same app-level intake modal
 * as the My Chart shortcut, via the `openIntakeQuestionnaire` query param.
 */
export function IntakeQuestionnaireBanner(): JSX.Element | null {
  const navigate = useNavigate();
  const { outstanding, loading } = useIntakeQuestionnaireOutstanding();

  if (loading || !outstanding) {
    return null;
  }

  return (
    <Alert color="polaris" variant="light" radius="md" p="lg">
      <Group justify="space-between" align="center" wrap="wrap" gap="md">
        <Group align="center" wrap="nowrap" gap="md" style={{ flex: '1 1 260px', minWidth: 240 }}>
          <ThemeIcon size={48} radius="md" variant="light" color="polaris" style={{ flexShrink: 0 }}>
            <IconClipboardText size={28} />
          </ThemeIcon>
          <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
            <Text fw={700} size="lg">
              Finish your sleep history questionnaire
            </Text>
            <Text size="sm" c="dimmed">
              It takes about 2–3 minutes. Completing it before your appointment saves time with your clinician.
            </Text>
          </Stack>
        </Group>
        <Button
          size="md"
          w={{ base: '100%', sm: 'auto' }}
          style={{ flexShrink: 0 }}
          onClick={() => navigate('/?openIntakeQuestionnaire=true')}
        >
          Complete questionnaire
        </Button>
      </Group>
    </Alert>
  );
}
